import React, { useEffect, useState } from "react";
import { Heart, BookOpen, Lightbulb, Shield, Flame, Users, Sparkles, TrendingUp } from "lucide-react";

const Yahffirmations = () => {
  // Iteration 6.1: live Pillar Pulse pulled from /api/analytics/vow-witnesses/stats
  // and surfaced inside the SIREN SUNDAY'S card (Sunday's "summary ack" section)
  const [pulse, setPulse] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/analytics/vow-witnesses/stats')
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (!cancelled && data) setPulse(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const PILLAR_COLORS = {
    YAH: '#D4AF37', HEALTH: '#4ADE80', WEALTH: '#B87333', HAPPINESS: '#F472B6',
  };
  const topPillar = pulse && pulse.total > 0
    ? Object.entries(pulse.by_pillar).reduce((a, b) => (a[1] >= b[1] ? a : b))[0]
    : null;

  const dailyAffirmations = [
    {
      day: "MND MONDAY'S",
      title: "Gratitude",
      color: "black",
      bgGradient: "from-gray-900 via-black to-gray-800",
      textColor: "text-yellow-400",
      icon: Heart,
      affirmation: "Gratitude begins when we acknowledge the Creative universal Source behind every blessing. His loyal love endures forever. Yah be praised always!",
      reflection: "What are you most thankful for in this moment?",
      principle: "For YHWH יהוה is good; His loyal love endures forever, and his faithfulness through all generations.",
      reference: "Psalm 100:5",
      frequency: "In service and stillness find your FLY Frequency."
    },
    {
      day: "TEACH ME TUESDAY'S",
      title: "Divine Names",
      color: "purple-blue",
      bgGradient: "from-purple-900 via-indigo-800 to-blue-900",
      textColor: "text-purple-300",
      icon: BookOpen,
      affirmation: "Call to Him in the way your heart knows best. Yahawah, yahuwah, yahweh, jehovah, Ancient of days. There are many names, yet one divine presence guiding our path. Yah be praised always!",
      reflection: "How do you connect with your Creator throughout your day?",
      principle: "May people know that you whose name is YHWH יהוה, you alone are the Most High over all the earth.",
      reference: "Psalm 83:18",
      frequency: "In service & stillness find your Devotion."
    },
    {
      day: "WISDOM WEDNESDAY'S",
      title: "Living Truth",
      color: "green",
      bgGradient: "from-emerald-900 via-green-800 to-teal-900",
      textColor: "text-emerald-300",
      icon: Lightbulb,
      affirmation: "The ancient principles of the \"Word\" are more than text — they are living laws written on the heart, Guidance & wisdom enabling us to operate in our best. Yah be praised always!",
      reflection: "What timeless truth do you live by today?",
      principle: "Sanctify them by means of the truth: Your word is truth.",
      reference: "John 17:17",
      frequency: "In service & stillness find your Guidance & Truth."
    },
    {
      day: "THANKFUL/THRIVING THURSDAY'S",
      title: "Divine Protection",
      color: "blue",
      bgGradient: "from-blue-900 via-cyan-800 to-blue-800",
      textColor: "text-cyan-300",
      icon: Shield,
      affirmation: "Every unseen rescue, every quiet mercy, Every helping hand, every loving gesture — a reminder of divine protection working on your behalf. Yah be praised always!",
      reflection: "Where have you recently felt protected?",
      principle: "The name of YHWH יהוה is a strong tower. Into it the righteous one runs and receives protection.",
      reference: "Proverbs 18:10",
      frequency: "In service & stillness find the protection of YHWH יהוה."
    },
    {
      day: "HAPPY FRIDAY'S",
      title: "Faith & Fiyah",
      color: "yellow",
      bgGradient: "from-yellow-700 via-amber-600 to-orange-600",
      textColor: "text-yellow-200",
      icon: Flame,
      affirmation: "Faith is the fiyah that reveals our purpose illuminating our path forward. Dance in that fiyah with the passion of fly frequency. Yah be praised always.",
      reflection: "What keeps your inner fiyah burning through challenges?",
      principle: "On the other hand, the fruitage of the spirit is love, joy, peace, patience, kindness, faith.",
      reference: "Galatians 5:22",
      frequency: "In service & stillness find your FAITH."
    },
    {
      day: "SACRED SATURDAY'S",
      title: "Family & Love",
      color: "orange",
      bgGradient: "from-orange-700 via-red-600 to-orange-800",
      textColor: "text-orange-200",
      icon: Users,
      affirmation: "Family is both A divine inheritance & an assignment where we provide love, patience, care, guidance and compassion. Yah be praised always!",
      reflection: "Who has shown you love that shaped your journey?",
      principle: "For this reason I bend my knees to the father to whom every family in heaven & on earth owes its name.",
      reference: "Ephesians 3:15",
      frequency: "In service & stillness love your FAMILY."
    },
    {
      day: "SIREN SUNDAY'S",
      title: "Inspiyah Culture",
      color: "red",
      bgGradient: "from-red-900 via-rose-800 to-red-800",
      textColor: "text-rose-300",
      icon: Sparkles,
      affirmation: "Sharing your faith a fly frequency to inspiyah culture is an act of praise — a way to keep ancient light alive & initiate the fiyah. Yah be praised always!",
      reflection: "What part of your story INSPIYAH's CULTURE?",
      principle: "For YHWH יהוה called us, not for uncleanness, but for holiness.",
      reference: "1 Thessalonians 4:7",
      frequency: "In service & stillness I, You, We INSPIYAH CULTURE."
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-40 h-40 border-2 border-purple-400 rotate-45 animate-pulse" style={{animationDuration: '4s'}} />
        <div className="absolute bottom-32 right-20 w-32 h-32 border border-orange-400 rounded-full animate-pulse" style={{animationDuration: '5s'}} />
        <div className="absolute top-1/2 left-1/3 text-6xl text-yellow-400/30 animate-pulse" style={{animationDuration: '6s'}}>✦</div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-3 mb-6">
            <Sparkles size={24} className="text-purple-600" />
            <span className="text-sm uppercase tracking-widest text-gray-500 font-light">
              Daily Divine Alignment
            </span>
            <Sparkles size={24} className="text-orange-600" />
          </div>
          <h2 className="text-5xl lg:text-6xl font-light tracking-tight text-black mb-6">
            <span className="bg-gradient-to-r from-purple-600 via-orange-500 to-yellow-500 bg-clip-text text-transparent">
              YAHFFIRMATIONS
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto font-light leading-relaxed">
            Sacred messages for each day of your week, aligning your frequency with YAH's eternal truth. 
            Ancient wisdom meeting modern living through daily divine inspiration.
          </p>
        </div>

        {/* Daily Affirmation Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {dailyAffirmations.map((item, index) => {
            const Icon = item.icon;
            return (
              <div 
                key={index}
                className={`bg-gradient-to-br ${item.bgGradient} rounded-2xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-2 group relative overflow-hidden border border-white/10`}
              >
                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full" />
                
                {/* Day Header */}
                <div className="flex items-center justify-between mb-6 relative z-10">
                  <div>
                    <h3 className={`text-sm uppercase tracking-widest ${item.textColor} font-medium mb-1 opacity-80`}>
                      {item.day}
                    </h3>
                    <div className="text-2xl font-light text-white">
                      {item.title}
                    </div>
                  </div>
                  <div className={`${item.textColor} opacity-60 group-hover:opacity-100 transition-opacity`}>
                    <Icon size={32} />
                  </div>
                </div>

                {/* Affirmation */}
                <div className="mb-6 relative z-10">
                  <div className="flex items-start space-x-3 mb-4">
                    <span className="text-2xl mt-1">✨</span>
                    <p className="text-white/90 font-light leading-relaxed italic text-sm">
                      "{item.affirmation}"
                    </p>
                  </div>
                </div>

                {/* Soul Reflection */}
                <div className="mb-6 bg-white/10 backdrop-blur-sm rounded-lg p-4 relative z-10">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-lg">💭</span>
                    <span className={`text-xs uppercase tracking-wider ${item.textColor} font-medium`}>
                      Soul Reflection
                    </span>
                  </div>
                  <p className="text-white/80 text-sm font-light italic">
                    {item.reflection}
                  </p>
                </div>

                {/* Ancient Principles */}
                <div className="mb-6 border-t border-white/20 pt-6 relative z-10">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="text-lg">📜</span>
                    <span className={`text-xs uppercase tracking-wider ${item.textColor} font-medium`}>
                      Ancient Principles
                    </span>
                  </div>
                  <p className="text-white/90 text-sm font-light leading-relaxed mb-2">
                    "{item.principle}"
                  </p>
                  <cite className="text-white/60 text-xs font-light">
                    — {item.reference}
                  </cite>
                </div>

                {/* FLY Frequency */}
                <div className="relative z-10">
                  <div className="flex items-start space-x-2">
                    <span className="text-base mt-0.5">🌿</span>
                    <p className={`${item.textColor} text-sm font-light italic`}>
                      {item.frequency}
                    </p>
                  </div>
                </div>

                {/* ─── ITERATION 6.1 — SIREN SUNDAY'S WEEKLY PILLAR PULSE ─── */}
                {item.day === "SIREN SUNDAY'S" && pulse && (
                  <div className="mt-6 pt-6 border-t border-white/20 relative z-10" data-testid="siren-sunday-pulse">
                    <div className="flex items-center space-x-2 mb-3">
                      <TrendingUp size={16} className={item.textColor} />
                      <span className={`text-xs uppercase tracking-wider ${item.textColor} font-medium`}>
                        Pillar Pulse — Weekly Ack
                      </span>
                    </div>
                    <p className="text-white/80 text-xs font-light leading-relaxed mb-4 italic">
                      {pulse.total > 0
                        ? `${pulse.total} ${pulse.total === 1 ? 'soul has' : 'souls have'} witnessed a vow this season${topPillar ? ` — ${topPillar} resonates most.` : '.'}`
                        : 'No vows witnessed yet. The portal awaits its first seeker.'}
                    </p>
                    <div className="grid grid-cols-4 gap-2">
                      {['YAH', 'HEALTH', 'WEALTH', 'HAPPINESS'].map((p) => {
                        const count = (pulse.by_pillar && pulse.by_pillar[p]) || 0;
                        return (
                          <div key={p} className="text-center py-2 px-1 bg-black/30 backdrop-blur-sm rounded">
                            <div className="text-[9px] tracking-widest uppercase mb-1" style={{ color: PILLAR_COLORS[p] }}>{p}</div>
                            <div className="text-xl font-light" style={{ color: PILLAR_COLORS[p] }}>{count}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="inline-block bg-gradient-to-r from-purple-600 via-orange-500 to-yellow-500 p-[2px] rounded-full">
            <div className="bg-white rounded-full px-8 py-3">
              <p className="text-sm font-light tracking-wide text-gray-700">
                <span className="font-medium bg-gradient-to-r from-purple-600 to-orange-500 bg-clip-text text-transparent">
                  Start each day
                </span>{" "}
                aligned with divine purpose
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Yahffirmations;